/* eslint-disable complexity */
import {
  ReportAssumption,
  ReportPaymentType,
  ReportSheetsAssumption,
  ReportSystemCalc
} from '@futrli-sdk/predict';
import { cloneDeep, get } from 'lodash';
import { IntlShape } from 'react-intl';

import {
  AssumptionTypes,
  REPORT_SECTION,
  SHEETS_ASSUMPTION,
  SYSTEM_CALC
} from '../../../../core/constants';
import {
  AssumptionRow,
  CalculationRow,
  PaymentTypeRow,
  RowsType,
  RowType,
  SheetsAssumptionRow,
  SystemCalcRow,
  TotalRow
} from './types';
import { PredictionsAccountCategories } from '../../../../core/types';
import {
  ROW_KIND,
  cumulativeTotalPreviousMonthCategories,
  rowKindsWithFYValue,
  staticIdsForFYCalculations
} from './constants';

export const FY_PREFIX = 'FY';

export const hasPadding = (row: RowType, index: number): boolean => {
  return (
    (row.kind === ROW_KIND.heading && index !== 0) ||
    (row.level === 0 &&
      index !== 0 &&
      !(row.kind === ROW_KIND.total && row.forceRowSize === 'medium' && row.childCount === 0))
  );
};

export function isAssumptionStyleRow(row: RowType) {
  return [ROW_KIND.assumption, ROW_KIND.paymentType, ROW_KIND.systemCalc].includes(row.kind as ROW_KIND);
}

export function isReportAssumption(
  assumptionOrPaymentType: ReportAssumption | ReportPaymentType | ReportSystemCalc | ReportSheetsAssumption
): assumptionOrPaymentType is ReportAssumption {
  return AssumptionTypes.includes((assumptionOrPaymentType as ReportAssumption).type);
}

export function isSystemCalc(
  assumptionOrPaymentType: ReportAssumption | ReportPaymentType | ReportSystemCalc | ReportSheetsAssumption
): assumptionOrPaymentType is ReportSystemCalc {
  return (assumptionOrPaymentType as ReportSystemCalc).type == SYSTEM_CALC;
}

export function isSheetsAssumption(
  assumptionOrPaymentType: ReportAssumption | ReportPaymentType | ReportSystemCalc | ReportSheetsAssumption
): assumptionOrPaymentType is ReportSheetsAssumption {
  return (assumptionOrPaymentType as ReportSheetsAssumption).type == SHEETS_ASSUMPTION;
}

/**
 * Returns indexes of `FY YEAR` elements in date columns array
 * @param columns date columns
 * @returns
 */
export const getFYIndexes = (columns: string[]) => {
  return columns.reduce((acc: number[], currValue: string, index: number) => {
    if (currValue.startsWith(FY_PREFIX)) {
      acc.push(index);
    }
    return acc;
  }, []);
};

/**
 * Updates total rows data with FY totals. FY totals are added on specific position determined by indexes of
 * `FY YEAR` label in extended columns array (date columns + FY YEAR labels)
 * @param totalRows calculated total rows
 * @param columns calculated columns
 */
export const getTotalAccountRowsWithFYInfo = (
  totalRows: TotalRow[],
  columns: string[],
  category?: PredictionsAccountCategories
) => {
  const newTotalRows = cloneDeep(totalRows);
  for (const row of newTotalRows) {
    if (!row.account) {
      continue;
    }
    const { period_data } = row.account;
    const fyIndexes = getFYIndexes(columns);

    if (category && cumulativeTotalPreviousMonthCategories.indexOf(category) !== -1) {
      for (const index of fyIndexes) {
        if (rowKindsWithFYValue.indexOf(row.kind) !== -1) {
          period_data.splice(index, 0, period_data[index > 0 ? index - 1 : 0]);
        } else {
          period_data.splice(index, 0, '-');
        }
      }
    } else {
      let intervalStart = 0;

      for (const index of fyIndexes) {
        const fySum = period_data
          .slice(intervalStart, index)
          .map(Number)
          .reduce((acc, curr) => (isNaN(curr) ? acc : acc + curr), 0);
        period_data.splice(index, 0, String(fySum));
        intervalStart = index + 1;
      }
    }
  }

  return newTotalRows;
};

/**
 * Adds FY (Financial Year) totals to period data by summing intervals between FY indexes.
 *
 * For example, if periodData = ['100', '200', '300'] and fyIndexes = [3],
 * this will sum 100 + 200 + 300 = 600 and insert it at index 3.
 * Result: ['100', '200', '300', '600']
 *
 * This is used for P&L categories where FY totals should be the sum of all periods.
 *
 * @param periodData - Array of period values as strings (mutated in place)
 * @param fyIndexes - Array of indexes where FY totals should be inserted
 */
const addFYTotalsToPeriodData = (periodData: string[], fyIndexes: number[]) => {
  let intervalStart = 0;

  for (const index of fyIndexes) {
    // Extract the slice of data for this FY interval (e.g., Jan-Dec for FY 2024)
    const fySum = periodData
      .slice(intervalStart, index)
      .map(Number)
      .reduce((acc, curr) => (isNaN(curr) ? acc : acc + curr), 0);

    // Insert the sum at the FY column position
    periodData.splice(index, 0, String(fySum));

    // Move to next interval (after the inserted FY column)
    intervalStart = index + 1;
  }
};

/**
 * Adds FY totals to period data for cumulative categories (Balance Sheet).
 *
 * For balance sheet items (assets, liabilities), the FY total should be the same as
 * the last month of the financial year because these are cumulative balances, not sums.
 *
 * For example, if Dec balance is 1000, the FY 2024 value should also be 1000 (not a sum).
 *
 * @param periodData - Array of period values as strings (mutated in place)
 * @param fyIndexes - Array of indexes where FY totals should be inserted
 * @param rowKind - Type of row to determine if it should show FY value or just '-'
 */
const addFYTotalsToPeriodDataCumulative = (periodData: string[], fyIndexes: number[], rowKind: ROW_KIND) => {
  for (const index of fyIndexes) {
    // Some row kinds should show the previous month's value
    if (rowKindsWithFYValue.indexOf(rowKind) !== -1) {
      // Copy the value from the previous column (last month of FY)
      periodData.splice(index, 0, periodData[index > 0 ? index - 1 : 0]);
    } else {
      // Other row kinds (like certain calculations) show '-' for FY columns
      periodData.splice(index, 0, '-');
    }
  }
};

/**
 * Calculates FY value for joined row (percentage row) based on static_id and stored FY values.
 *
 * Joined rows show percentages relative to income. The formulas are:
 * - Gross Profit %: gross_profit / income
 * - Operating Profit %: operating_profit / income
 * - Net Profit %: net_profit / (income + other_income)
 *
 * This ensures the percentage shown in FY columns is calculated from FY totals,
 * not from averaging monthly percentages (which would be mathematically incorrect).
 *
 * @param staticId - The static_id of the current row (e.g., 'gross_profit')
 * @param fyArrayIndex - The index in the FY values array (0 for first FY, 1 for second FY, etc.)
 * @param fyValuesByStaticId - Map of static_id to array of FY values
 * @returns Calculated percentage as string, or '-' if calculation not possible
 */
const calculateJoinedRowFYValue = (
  staticId: string | null,
  fyArrayIndex: number,
  fyValuesByStaticId: Record<string, string[]>
): string => {
  if (staticId === 'gross_profit') {
    // gross_profit_joined_row_FY = gross_profit_FY / income_FY
    const grossProfitFY = fyValuesByStaticId.gross_profit?.[fyArrayIndex];
    const incomeFY = fyValuesByStaticId.income?.[fyArrayIndex];

    if (grossProfitFY && incomeFY) {
      const grossProfitNum = Number(grossProfitFY);
      const incomeNum = Number(incomeFY);
      if (!isNaN(grossProfitNum) && !isNaN(incomeNum) && incomeNum !== 0) {
        return String(grossProfitNum / incomeNum);
      }
    }
  } else if (staticId === 'operating_profit') {
    // operating_profit_joined_row_FY = operating_profit_FY / income_FY
    const operatingProfitFY = fyValuesByStaticId.operating_profit?.[fyArrayIndex];
    const incomeFY = fyValuesByStaticId.income?.[fyArrayIndex];

    if (operatingProfitFY && incomeFY) {
      const operatingProfitNum = Number(operatingProfitFY);
      const incomeNum = Number(incomeFY);
      if (!isNaN(operatingProfitNum) && !isNaN(incomeNum) && incomeNum !== 0) {
        return String(operatingProfitNum / incomeNum);
      }
    }
  } else if (staticId === 'net_profit') {
    // net_income_joined_row_FY = net_profit_FY / (income_FY + other_income_FY)
    const netProfitFY = fyValuesByStaticId.net_profit?.[fyArrayIndex];
    const incomeFY = fyValuesByStaticId.income?.[fyArrayIndex];
    const otherIncomeFY = fyValuesByStaticId.other_income?.[fyArrayIndex];

    if (netProfitFY) {
      const netProfitNum = Number(netProfitFY);
      const incomeNum = Number(incomeFY);
      const otherIncomeNum = Number(otherIncomeFY);

      if (!isNaN(netProfitNum)) {
        // Treat NaN values as 0 in the denominator (allows calculation if at least one value is valid)
        const incomeValue = isNaN(incomeNum) ? 0 : incomeNum;
        const otherIncomeValue = isNaN(otherIncomeNum) ? 0 : otherIncomeNum;
        const denominator = incomeValue + otherIncomeValue;

        // Only return '-' if both values are NaN (denominator would be 0) or sum is 0
        if (denominator !== 0) {
          return String(netProfitNum / denominator);
        }
      }
    }
  }

  return '-';
};

/**
 * Builds a map of FY values for rows with specific static_ids that need calculation.
 *
 * This function extracts FY values for key rows (income, gross_profit, operating_profit,
 * net_profit, other_income) so they can be used in joined row percentage calculations.
 *
 * For example, if we have:
 * - income row with FY values [10000, 12000]
 * - gross_profit row with FY values [4000, 5000]
 *
 * The result will be: { income: ['10000', '12000'], gross_profit: ['4000', '5000'] }
 *
 * @param rows - Array of account rows
 * @param fyIndexes - Array of indexes where FY columns are located
 * @returns Map of static_id to array of FY values
 */
const buildFYValuesByStaticId = (rows: RowsType, fyIndexes: number[]): Record<string, string[]> => {
  const fyValuesByStaticId: Record<string, string[]> = {};

  for (const row of rows) {
    const staticId = 'static_id' in row.account ? row.account.static_id : undefined;
    // Only collect FY values for rows that are used in percentage calculations
    if (staticId && staticIdsForFYCalculations.includes(staticId)) {
      // Extract the value at each FY column index
      fyValuesByStaticId[staticId] = fyIndexes.map(index => row.account.period_data[index]);
    }
  }

  return fyValuesByStaticId;
};

/**
 * Processes joined row FY values for calculation rows.
 *
 * Joined rows display percentage values (e.g., Gross Profit % = Gross Profit / Income).
 * For monthly columns, these percentages are calculated from monthly values.
 * For FY columns, we need to calculate the percentage from FY totals.
 *
 * This function:
 * 1. Identifies rows that have a joined row (percentage row)
 * 2. Calculates the correct FY percentage using FY totals
 * 3. Inserts the calculated value into the joined row's period data
 *
 * @param row - Current account row
 * @param fyIndexes - Array of indexes where FY columns are located
 * @param fyValuesByStaticId - Map of FY values for key rows (income, profit, etc.)
 */
const processJoinedRowFYValues = (row: RowType, fyIndexes: number[], fyValuesByStaticId: Record<string, string[]>) => {
  // Check if this is a calculation row with a joined (percentage) row
  const isJoinedRow = row.kind === ROW_KIND.calculation && (row as CalculationRow).joinedRow !== undefined;
  if (!isJoinedRow) {
    return;
  }

  const joinedRow = (row as CalculationRow).joinedRow;
  const joinedRowPeriodData = joinedRow?.account.period_data;

  if (joinedRowPeriodData) {
    // Get the static_id to determine which formula to use
    const currentStaticId = 'static_id' in row.account ? row.account.static_id : undefined;

    // Calculate and insert FY percentage for each FY column
    fyIndexes.forEach((fyIndex, fyArrayIndex) => {
      const fyValue = calculateJoinedRowFYValue(currentStaticId || null, fyArrayIndex, fyValuesByStaticId);
      joinedRowPeriodData.splice(fyIndex, 0, fyValue);
    });
  }
};

/**
 * Updates account rows data with FY (Financial Year) totals.
 *
 * This function processes account rows in three stages:
 *
 * 1. **Add FY totals to period data**: For each row, calculates and inserts FY column values.
 *    - P&L categories (income, expenses): Sum all months in the FY
 *    - Balance Sheet categories (assets, liabilities): Copy the last month's value
 *
 * 2. **Build FY values map**: Extract FY totals for key rows (income, profits) that are
 *    needed for percentage calculations in joined rows.
 *
 * 3. **Process joined rows**: For calculation rows with percentage displays (joined rows),
 *    calculate the correct FY percentage using FY totals instead of averaging monthly percentages.
 *
 * Example:
 * - Monthly: Income [1000, 1200], Gross Profit [400, 500], GP% [40%, 41.67%]
 * - FY: Income 2200, Gross Profit 900, GP% should be 900/2200 = 40.91% (not average of 40% and 41.67%)
 *
 * @param accountRows - Array of account rows to process
 * @param columns - Array of column headers (includes month names and 'FY YEAR' labels)
 * @param category - Optional category type to determine calculation method (P&L vs Balance Sheet)
 * @returns New array of account rows with FY totals inserted
 */
export const getAccountRowsWithFYInfo = (
  accountRows: RowsType,
  columns: string[],
  category?: PredictionsAccountCategories
) => {
  const newAccountRows = cloneDeep(accountRows);
  const fyIndexes = getFYIndexes(columns);

  // Step 1: Add FY totals to all rows
  // Different calculation methods for P&L (sum) vs Balance Sheet (last month value)
  for (const row of newAccountRows) {
    const { period_data } = row.account;

    if (category && cumulativeTotalPreviousMonthCategories.indexOf(category) !== -1) {
      // Balance Sheet: Use last month's value as FY total
      addFYTotalsToPeriodDataCumulative(period_data, fyIndexes, row.kind);
    } else {
      // P&L: Sum all months for FY total
      addFYTotalsToPeriodData(period_data, fyIndexes);
    }
  }

  // Step 2: Build FY values map for calculations
  // Extract FY totals from key rows (income, profits) for use in percentage calculations
  const fyValuesByStaticId = buildFYValuesByStaticId(newAccountRows, fyIndexes);

  // Step 3: Process joined rows with FY calculations
  // Calculate correct FY percentages for joined (percentage) rows
  for (const row of newAccountRows) {
    processJoinedRowFYValues(row, fyIndexes, fyValuesByStaticId);
  }

  return newAccountRows;
};

/**
 * Updates account rows data with FY totals for balance sheet. FY totals for balance sheet should
 * show the same figure as the financial year end month because balance sheet totals are cumulative.
 * @param accountRows current account rows without FY totals
 * @param columns calculated columns with FY total columns
 * @returns Updated account rows with FY info for each FY column
 */
export const getBalanceSheetAccountRowsWithFYInfo = (accountRows: RowsType, columns: string[]) => {
  const newAccountRows = cloneDeep(accountRows);
  for (const row of newAccountRows) {
    const { period_data } = row.account;
    const fyIndexes = getFYIndexes(columns);
    for (const index of fyIndexes) {
      if (rowKindsWithFYValue.indexOf(row.kind) !== -1) {
        period_data.splice(index, 0, period_data[index > 0 ? index - 1 : 0]);
      } else {
        period_data.splice(index, 0, '-');
      }
    }
  }

  return newAccountRows;
};

const getSystemCalcRowName = (intl: IntlShape, account: ReportSystemCalc) => {
  if (account.system_assumption_type === 'vat-payments') {
    return intl.formatMessage({ id: 'predict.vat_gst_payment', defaultMessage: 'VAT / GST payment' });
  }
  if (account.system_assumption_type === 'year-end') {
    return intl.formatMessage({ id: 'predict.year_end', defaultMessage: 'Year end transfer' });
  }
  if (account.system_assumption_type === 'unpaid-invoices') {
    if (get(account, 'extra_display_params.invoices_or_bills') === 'invoices') {
      return intl.formatMessage({ id: 'predict.unpaid_invoices', defaultMessage: 'Unpaid invoices' });
    }
    if (get(account, 'extra_display_params.invoices_or_bills') === 'bills') {
      return intl.formatMessage({ id: 'predict.unpaid_bills', defaultMessage: 'Unpaid bills' });
    }
  }

  return account.name;
};

export const formatChildRows = (
  intl: IntlShape,
  reportSection: REPORT_SECTION,
  childReportItems: (ReportAssumption | ReportPaymentType | ReportSheetsAssumption | ReportSystemCalc)[],
  startingLevel: number,
  parentName: string
): RowsType => {
  const sortedChildReportItems = [...childReportItems].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
  return sortedChildReportItems.map((childReportItem, assumptionIndex) => {
    const baseChild = {
      parentName,
      section: reportSection,
      siblingCount: childReportItems.length,
      siblingIndex: assumptionIndex,
      padding: false,
      level: startingLevel + 1
    };

    if (isReportAssumption(childReportItem)) {
      return {
        ...baseChild,
        kind: ROW_KIND.assumption,
        account: childReportItem
      } as AssumptionRow;
    } else if (isSystemCalc(childReportItem)) {
      return {
        ...baseChild,
        kind: ROW_KIND.systemCalc,
        account: {
          ...childReportItem,
          name: getSystemCalcRowName(intl, childReportItem)
        }
      } as SystemCalcRow;
    } else if (isSheetsAssumption(childReportItem)) {
      return {
        ...baseChild,
        kind: ROW_KIND.sheetsAssumption,
        account: childReportItem
      } as SheetsAssumptionRow;
    }
    return {
      ...baseChild,
      kind: ROW_KIND.paymentType,
      account: childReportItem
    } as PaymentTypeRow;
  });
};
